import { createContext, memo, useContext, useEffect, useRef, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Badge, CompanyTag } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { ReferralMaterialsPageKey } from 'api/ClientArea/ReferralMaterialsDto'
import { NameDto } from 'api/Common/Types'
import { Link } from 'components/Link'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

export const GridResizeContext = createContext(0)

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

function LimitedLinkList({
  items,
  max,
  getLinkPath,
}: {
  items: NameDto<string>[]
  max: number
  getLinkPath: (x: NameDto<string>) => string
}) {
  const [expanded, setExpanded] = useState(false)

  if (!items.length) {
    return <td style={{ padding: '0.5rem' }}>-</td>
  }

  const visibleItems = expanded ? items : items.slice(0, max)

  return (
    <td style={{ padding: '0.5rem' }}>
      {visibleItems.map((item) => (
        <Link
          key={item.id}
          to={getLinkPath(item)}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: '0.25rem',
            color: '#2563eb',
            textDecoration: 'underline',
          }}
        >
          {item.name || '-'}
        </Link>
      ))}

      {items.length > max && (
        <Button
          variant='link'
          style={{
            marginTop: '0.25rem',
            padding: 0,
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide' : 'Expand'}
        </Button>
      )}
    </td>
  )
}
function LimitedTextList({ items, max }: { items: { targetUrl: string }[]; max: number }) {
  const [expanded, setExpanded] = useState(false)

  if (!items.length) {
    return <td className='columns__content'>-</td>
  }

  const visibleItems = expanded ? items : items.slice(0, max)

  return (
    <td className='columns__content'>
      {visibleItems.map(({ targetUrl }, index) => (
        <div className='table-columns__resizable' key={`${targetUrl}-${index}`}>
          {targetUrl || '-'}
        </div>
      ))}

      {items.length > max && (
        <Button
          variant='link'
          style={{
            marginTop: '0.25rem',
            padding: 0,
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide' : 'Expand'}
        </Button>
      )}
    </td>
  )
}

const ResizableTextContent = memo(({ content }: { content: string }) => {
  const resizeCount = useContext(GridResizeContext)
  const [expanded, setExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkTruncation = () => {
      if (contentRef.current) {
        const wasExpanded = expanded

        if (wasExpanded) {
          contentRef.current.className = 'table-columns__resizable-content'
        }

        const wouldBeTruncated = contentRef.current.scrollHeight > contentRef.current.clientHeight

        if (wasExpanded) {
          contentRef.current.className = ''
        }

        setIsTruncated(wouldBeTruncated)

        if (wasExpanded && !wouldBeTruncated) {
          setExpanded(false)
        }
      }
    }

    checkTruncation()

    const observer = new ResizeObserver(() => {
      checkTruncation()
    })

    if (contentRef.current) {
      observer.observe(contentRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [content, expanded, resizeCount])

  if (!content) {
    return <div>-</div>
  }

  return (
    <>
      <div
        ref={contentRef}
        className={expanded ? '' : 'table-columns__resizable-content'}
        style={expanded ? { whiteSpace: 'normal', wordBreak: 'break-word' } : {}}
      >
        {content}
      </div>
      {isTruncated && (
        <Button
          variant='link'
          style={{
            marginTop: '0.25rem',
            padding: 0,
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Hide' : 'Expand'}
        </Button>
      )}
    </>
  )
})

const ResizableTextCell = memo(({ content }: { content: string }) => {
  return (
    <td className='columns__content'>
      <ResizableTextContent content={content} />
    </td>
  )
})

export const referralMaterialsColumn: GridColumn[] = [
  {
    title: 'Referral Material Name',
    field: ReferralMaterialsPageKey.name,
    cell: (props: GridCellProps) => (
      <td className='table-columns__link'>
        <Link
          to={generatePath(links.referralMaterials.detailsUrl as string, {
            id: props.dataItem.id,
            slug: 'details',
          })}
        >
          {props.dataItem.name}
        </Link>
      </td>
    ),
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Type',
    field: ReferralMaterialsPageKey.type,
    cell: (props: GridCellProps) => (
      <td className='columns__content'>{props.dataItem.type.name}</td>
    ),
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Category',
    field: ReferralMaterialsPageKey.categories,
    cell: (props: GridCellProps) => (
      <LimitedLinkList
        items={props.dataItem.categories || []}
        max={4}
        getLinkPath={(item) =>
          generatePath(links.referralMaterialsCategories.detailsUrl as string, {
            id: item.id,
            slug: 'details',
          })
        }
      />
    ),
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Size(s)',
    field: ReferralMaterialsPageKey.sizes,
    cell: (props: GridCellProps) => {
      const sizes = props.dataItem.sizes || []
      const content = sizes.map((size: NameDto) => size.name).join('; ')
      return <ResizableTextCell content={content} />
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Target URL',
    field: ReferralMaterialsPageKey.path,
    cell: (props: GridCellProps) => (
      <LimitedTextList items={props.dataItem.targetUrls || []} max={4} />
    ),
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Language',
    field: ReferralMaterialsPageKey.language,
    cell: (props: GridCellProps) => {
      const languages = props.dataItem.caLanguages || []
      const content = languages.map((lang: NameDto) => lang.name).join(', ')
      return <ResizableTextCell content={content} />
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    minResizableWidth: 120,
  },
  {
    title: 'Entity',
    field: ReferralMaterialsPageKey.entity,
    cell: (props: GridCellProps) => {
      const tickmillCompany = props.dataItem.tickmillCompany
      if (!tickmillCompany?.id) return <td />

      return (
        <td style={{ textAlign: 'center' }}>
          <CompanyTag id={tickmillCompany.id}>{tickmillCompany.name}</CompanyTag>
        </td>
      )
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 100,
    minResizableWidth: 80,
  },
  {
    title: 'Active',
    field: ReferralMaterialsPageKey.active,
    cell: (props: GridCellProps) => {
      const isActive =
        props.dataItem.enabled &&
        (props.dataItem.endDateTime == null || new Date(props.dataItem.endDateTime) > new Date())

      return (
        <td width={80} style={{ textAlign: 'center' }}>
          <Badge variant={isActive ? 'green' : 'red'}>{isActive ? 'Yes' : 'No'}</Badge>{' '}
        </td>
      )
    },
    show: true,
    className: 'table-columns__content',
    filter: 'text',
    width: 100,
    minResizableWidth: 80,
  },
  {
    title: 'End Date',
    field: ReferralMaterialsPageKey.endDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.endDateTime, true)}
      </td>
    ),
    show: true,
    className: 'table-columns__date',
    filter: 'text',
    width: 125,
  },
  {
    title: 'Last Updated',
    field: ReferralMaterialsPageKey.updatedDate,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>
        {formatLocalDateUTC(props.dataItem.updatedDate, true)}
      </td>
    ),
    show: true,
    className: 'table-columns__date',
    filter: 'text',
    width: 125,
  },
]
